<script lang="ts">
  import Filter from '$icons/FilterIcon.svelte';
  import Sort from '$icons/SortIcon.svelte';
  import DefaultTabsWrapper from '$lib/components/homepage/tabs/functional/wrappers/DefaultTabsWrapper.svelte';
  import SearchBar from '$lib/components/homepage/SearchBar.svelte';
  import DefaultTab from '$lib/components/homepage/tabs/functional/DefaultTab.svelte';
  import { hometabs } from './tabs/hometabs';
  import type { DynamicSearchParams } from '$src/types/homepage.interface';
  import type { TabData } from '$lib/components/homepage/tabs/functional/wrappers';
  import { useScrollVersion } from './top-island';
  import { DropdownItem } from '$lib/components/common/dropdowns/dropdown';
  import type { SortOption } from '$src/utils/api/feed.util';
  import { createEventDispatcher, onMount } from 'svelte';
  import NavHamburgerIcon from '$icons/NavHamburgerIcon.svelte';
  import Dropdown from '$lib/components/common/dropdowns/Dropdown.svelte';
  import Button from '$lib/components/common/buttons/Button.svelte';
  import UserAvatarWrapper from '../common/user/userAvatarWrapper.svelte';
  import { me } from '$src/stores/user.store';
  import { goto } from '$app/navigation';
  import { isMobile } from '$stores/media.store';
  import { slide } from 'svelte/transition';
  import SearchBarTab from '$lib/components/homepage/tabs/functional/SearchBarTab.svelte';
  import GardenLogoAltIcon from '$lib/icons/Logo.svelte';
  import StreamIsland from './StreamIsland.svelte';
  import TopActivityIsland from './TopActivityIsland.svelte';
  import TopDonorsIsland from './TopDonorsIsland/TopDonorsIsland.svelte';
  import { getStream } from '$src/utils/api/admin/streams.util';
  import TopIslandSortSelector from './_Components/TopIslandSortSelector.svelte';

  const dispatch = createEventDispatcher<{
    clickEditFilters: { searchTab: DynamicSearchParams['type'] };
    peopleSortChanged: { sort: (typeof sortOptions)[number] };
    sortChanged: { sort: (typeof sortOptions)[number] };
    searchbarFocus: never | undefined;
    tabClick: { tab: string };
    clickedHamburger: string;
  }>();

  const searchTabs: TabData<DynamicSearchParams['type']>[] = [
    { title: 'Posts', value: 'posts' },
    { title: 'People', value: 'users' }
  ];

  export let selectedTab: (typeof hometabs)[number] = hometabs[0];
  export let searchParams: DynamicSearchParams = {
    type: 'posts',
    sortBy: null,
    filters: null,
    searchTerm: null
  };
  export let searchTerm: string;

  let typingSearch: string;
  let loading = false;

  $: typingSearch = searchTerm;

  let handleEnterKeyPress = () => {
    return null;
  };

  let selectedSearchTab: TabData<DynamicSearchParams['type']> = searchTabs[0];
  $: searchParams.type = selectedSearchTab.value;
  $: selectedSearchTab, (searchTerm = '');

  $: if (selectedTab.value !== 'explore') {
    selectedSearchTab = searchTabs[0];
  }

  const tabToSortOptions: Record<
    `${(typeof hometabs)[number]['value']}-${string | DynamicSearchParams['type']}`,
    { label: string; id: SortOption }[]
  > = {
    'for-you-default': [
      { label: 'Newest to oldest', id: 'new-to-old' },
      { label: 'Oldest to newest', id: 'old-to-new' },
      { label: 'Seeds - most to least', id: 'seeds-most-to-least' },
      { label: 'Seeds - least to most', id: 'seeds-least-to-most' }
    ],
    'explore-posts': [
      { label: 'Algorithmic', id: 'algorithmic' },
      { label: 'Newest to oldest', id: 'new-to-old' },
      { label: 'Oldest to newest', id: 'old-to-new' },
      { label: 'Seeds - most to least', id: 'seeds-most-to-least' },
      { label: 'Seeds - least to most', id: 'seeds-least-to-most' }
    ],
    'explore-users': [
      { label: 'Newest to oldest', id: 'new-to-old' },
      { label: 'Oldest to newest', id: 'old-to-new' },
      { label: 'Recent Activity', id: 'recent-activity' },
      { label: 'Popularity', id: 'popularity' },
      { label: 'Seeds Recieved', id: 'seed-received' }
    ],
    'galleries-default': [
      { label: 'Newest to oldest', id: 'new-to-old' },
      { label: 'Oldest to newest', id: 'old-to-new' }
    ],
    'challenges-': []
  };

  let sortOptions = [];
  let sortDropdown: Dropdown<unknown>;
  let selectedSortOption: DropdownItem<SortOption> = sortOptions[0];

  function updateSortOptions() {
    const opts =
      selectedTab.value == 'explore'
        ? tabToSortOptions[`${selectedTab.value}-${selectedSearchTab.value ?? 'posts'}`]
        : tabToSortOptions[`${selectedTab.value}-default`];

    if (!opts) return;

    sortOptions = opts.map(i => {
      return {
        variant: 'label',
        value: i.id,
        id: i.id,
        data: { label: i.label },
        customData: i.id
      };
    });

    onSortOptionUpdate(sortOptions[selectedTab.value == 'explore' ? 1 : 0]);
  }

  function onSortOptionUpdate(option: DropdownItem<SortOption>) {
    // console.log('💵 Sort option updated', option);

    if (selectedTab.value == 'explore' && selectedSearchTab.value == 'users') {
      dispatch('peopleSortChanged', { sort: option });
    } else {
      dispatch('sortChanged', { sort: option });
    }
  }

  $: selectedTab, selectedSearchTab, updateSortOptions();

  // let useOverflowHidden = false;

  // useScrollVersion.subscribe(_useScrollVersion => {
  //   if (_useScrollVersion) {
  //     useOverflowHidden = true;
  //     sortDropdown?.close();
  //   } else {
  //     setTimeout(() => (useOverflowHidden = false), 200);
  //   }
  // });

  let currentStream;
  onMount(async () => {
    currentStream = await getStream();
  });
</script>

<div
  class="--updated-border fixed shadow-island rounded-3xl bg-gray-50 top-0 right-0 left- z-[30] w-full
  lg:mx-auto lg:max-w-screen-lg lg:ml-10 lg:left-1/2 lg:transform lg:-translate-x-1/2"
  class:lg:top-0={$useScrollVersion}
  class:lg:top-4={!$useScrollVersion}
>
  <div
    class="flex flex-col --updated-border-outline bg-white top-0 inset-x-0 transition-all duration-200 rounded-b-10"
    class:lg:rounded-2xl={!$useScrollVersion}
    class:lg:p-4={!$useScrollVersion}
    class:lg:px-12={$useScrollVersion}
  >
    <div
      class="lg:hidden transition-all duration-200 flex justify-between items-center px-5 py-3 {$useScrollVersion
        ? 'h-0 -mt-8'
        : 'h-[48px]'}"
    >
      <button on:click={() => dispatch('clickedHamburger')}>
        <NavHamburgerIcon />
      </button>
      <button on:click={() => goto('/')}>
        <GardenLogoAltIcon width="30" />
      </button>
      <button
        on:click={() => {
          if ($me) {
            goto('/profile');
          } else {
            goto('/login');
          }
        }}
      >
        <div class="rounded-full flex items-center justify-center h-7 w-7">
          <UserAvatarWrapper
            cls={'w-[90%] aspect-square'}
            profileImage={$me?.profileImage}
            userName={$me?.userName}
          />
        </div>
      </button>
    </div>

    <div class="pt-2">
      <DefaultTabsWrapper
        tabs={hometabs}
        tabComponent={DefaultTab}
        bind:selectedTab
        on:clickTab={ev => {
          dispatch('tabClick', { tab: ev.detail.tabData.value });
        }}
        bottomBorder
      />
    </div>

    <div
      class="flex justify-center items-center gap-1 overflow-hidden 
      {$useScrollVersion ? 'h-0' : 'h-[48px]'}"
      class:mt-4={!$useScrollVersion}
      class:mb-4={$isMobile && !$useScrollVersion}
      transition:slide
    >
      <div class=" flex w-full gap-1 px-1">
        <SearchBar
          bind:loading
          on:focus={() => dispatch('searchbarFocus')}
          on:click={() => dispatch('searchbarFocus')}
          enteredValue={handleEnterKeyPress}
          bind:typingSearch
          dropDown={false}
          placeholder={selectedSearchTab.value === 'posts'
            ? 'Search for talent...'
            : 'Search for creators'}
        >
          <span slot="right-slot">
            <DefaultTabsWrapper
              tabs={searchTabs}
              tabComponent={SearchBarTab}
              bind:selectedTab={selectedSearchTab}
              disabled={selectedTab.value !== 'explore'}
            />
          </span>
        </SearchBar>

        <Button
          label="Filters"
          secondary
          on:click={() =>
            dispatch('clickEditFilters', {
              searchTab: selectedTab.value == 'galleries' ? 'galleries' : selectedSearchTab.value
            })}
          iconLeft={Filter}
          cls="h-[48px]"
        />

        <TopIslandSortSelector
          items={sortOptions}
          bind:selected={selectedSortOption}
          on:itemSelected={e => {
            onSortOptionUpdate(e.detail.item);
            selectedSortOption = e.detail.item;
            console.log('parent', e.detail.item);
          }}
        />
      </div>
      <!--  We changing this since its triggering flaky dispatches  -->
      <!-- Seems that its the layout shift that makes the dispatches  -->

      <!-- <Dropdown
        class="h-[48px]"
        variant="secondary-button-pick"
        variantOptions={{ variant: 'secondary-button-pick', buttonCls: 'h-[48px]' }}
        bind:this={sortDropdown}
        bind:selected={selectedSortOption}
        items={sortOptions}
        primaryIcon={Sort}
        on:itemSelected={e => {
          console.log('itemSelected', e.detail.item);
        }}
      /> -->
    </div>

    {#if selectedTab.value === 'for-you'}
      <div
        class="hidden absolute overflow-scroll pt-4 lg:block rounded-2xl md:max-w-[332px] w-full h-screen right-0 transition-all scrollbar-hide"
        class:top-[3rem]={$useScrollVersion}
        class:top-[9.4rem]={!$useScrollVersion}
      >
        <div
          class="flex-col gap-2 overflow-y-scroll pl-4 h-full static scrollbar-hide"
          class:pt-4={useScrollVersion}
        >
          <StreamIsland stream={currentStream} />
          <TopActivityIsland />
          <div class="mt-4">
            <TopDonorsIsland />
          </div>
        </div>
      </div>
    {/if}
  </div>
</div>
